import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getAllLampComponents } from '../services/productService';
import './About.css';

export default function About() {
  const [components, setComponents] = useState(null);

  useEffect(() => {
    getAllLampComponents().then(data => {
      setComponents({
        bases: data.bases,
        shades: data.shades,
        colors: data.colors,
      });
    }).catch(() => {});
  }, []);

  const baseCount = components?.bases?.length || 0;
  const shadeCount = components?.shades?.length || 0;
  const colorCount = components?.colors?.length || 0;
  const combinations = baseCount * shadeCount * colorCount * colorCount;
  const pairings = baseCount * shadeCount;
  const formatNum = (n) => n > 1000 ? `${Math.floor(n / 1000).toLocaleString()},000+` : String(n);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <motion.div
          className="about-hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.span className="about-badge" variants={itemVariants}>
            Our Story
          </motion.span>
          <motion.h1 className="about-title" variants={itemVariants}>
            About Kalpavriksha
          </motion.h1>
          <motion.p className="about-subtitle" variants={itemVariants}>
            A design-driven lighting studio specializing in modular, 3D-printed lamps.
            We combine industrial design, digital fabrication, and thoughtful materials
            to create customizable lamps that fit modern interiors.
          </motion.p>
        </motion.div>
      </section>

      {/* Philosophy Section */}
      <section className="about-content-section">
        <div className="about-container">
          <motion.div
            className="about-content-grid"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="about-text-block">
              <h2 className="about-section-title">Our Philosophy</h2>
              <p className="about-paragraph">
                Instead of mass-produced lighting, Kalpavriksha focuses on made-to-order
                3D-printed lamps. Each lamp is built from interchangeable components so you
                can choose your base, shade, and color. Our premium matte PLA material and
                minimal silhouettes give every piece a refined, designer-grade look and feel.
              </p>
              <p className="about-paragraph">
                By using 3D printing, we reduce waste, avoid overproduction, and offer far
                more variation than traditional manufacturing. Kalpavriksha lamps are ideal
                for design enthusiasts, interior designers, and anyone looking for unique,
                customizable home decor that blends technology with a warm, human touch.
              </p>
            </div>

            <div className="about-features">
              <div className="about-feature-card">
                <div className="about-feature-icon">✦</div>
                <h3>Made to Order</h3>
                <p>
                  Every lamp is 3D-printed specifically for your configuration, minimizing
                  waste and excess inventory.
                </p>
              </div>
              <div className="about-feature-card">
                <div className="about-feature-icon">✦</div>
                <h3>Modular Design</h3>
                <p>
                  Update or reconfigure your lamp by swapping components instead of
                  replacing the entire fixture.
                </p>
              </div>
              <div className="about-feature-card">
                <div className="about-feature-icon">✦</div>
                <h3>Premium Materials</h3>
                <p>
                  High-quality matte PLA produces a smooth, refined finish ideal for
                  modern lighting.
                </p>
              </div>
              <div className="about-feature-card">
                <div className="about-feature-icon">✦</div>
                <h3>Design Freedom</h3>
                <p>
                  {components ? baseCount : '—'} bases, {components ? shadeCount : '—'} shades, and {components ? colorCount : '—'} curated colors give you endless possibilities
                  to create your perfect lamp.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="about-process-section">
        <div className="about-container">
          <motion.div
            className="about-process-content"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="about-section-title center">How We Craft Each Lamp</h2>
            <div className="about-process-grid">
              <div className="process-step">
                <div className="process-number">01</div>
                <h3>You Design</h3>
                <p>
                  Use our interactive configurator to compose your perfect combination of
                  base, shade, and color.
                </p>
              </div>
              <div className="process-step">
                <div className="process-number">02</div>
                <h3>We Print</h3>
                <p>
                  Your lamp is 3D-printed layer by layer in premium matte PLA with
                  precision and care.
                </p>
              </div>
              <div className="process-step">
                <div className="process-number">03</div>
                <h3>Hand Assembly</h3>
                <p>
                  Each component is inspected, assembled, and packaged by hand to ensure
                  quality.
                </p>
              </div>
              <div className="process-step">
                <div className="process-number">04</div>
                <h3>Delivered to You</h3>
                <p>
                  Your custom lamp arrives ready to illuminate your space with warmth and
                  elegance.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="about-impact-section">
        <div className="about-container">
          <motion.div
            className="about-impact-content"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="about-section-title center">Designed for Modern Life</h2>
            <p className="about-impact-text">
              Kalpavriksha lamps are designed for indoor spaces such as bedrooms, living
              rooms, home offices, and creative studios. Their compact footprint makes them
              suitable for nightstands, desks, side tables, and shelves. Each lamp is more
              than illumination—it's a small piece of architecture composed by you,
              thoughtfully crafted to evolve with your space.
            </p>
            <div className="about-impact-stats">
              <div className="impact-stat">
                <span className="impact-number">{components ? formatNum(combinations) : '—'}</span>
                <span className="impact-label">Unique Combinations</span>
              </div>
              <div className="stat-divider" />
              <div className="impact-stat">
                <span className="impact-number">{components ? pairings.toLocaleString() : '—'}</span>
                <span className="impact-label">Possible Pairings</span>
              </div>
              <div className="stat-divider" />
              <div className="impact-stat">
                <span className="impact-number">Zero</span>
                <span className="impact-label">Waste Production</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <motion.div
          className="about-cta-content"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="about-cta-title">Ready to Design Your Lamp?</h2>
          <p className="about-cta-description">
            Experience the freedom of creating a lamp that's uniquely yours. Explore our
            configurator and bring your vision to life.
          </p>
          <Link to="/design" className="about-cta-button">
            <span className="button-text">Start Designing</span>
            <svg className="button-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
